import React from 'react';
import { styled } from '@mui/material/styles';
import { Box, Button, Typography } from '@mui/material';

import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import * as accountingSlice from '../../../data/accounting.slice';

import ProviderPricingDialog from './ProviderPricingDialog';
import ProviderPricingList from './ProviderPricingList';
import ActivateDialog from '../../ActivateDialog';
import ConfirmationDialog from '../../ConfirmationDialog';
import { BaseProvider, Provider } from '../../../data/models/provider.model';
import { ProviderServicePricing } from '../../../data/models/provider-service-pricing.model';
import { useLoggedInProviderId } from '../../../hooks/useLoggedInProviderId';
import { useIsPRUser } from '../../../hooks/permissions/useIsPRUser';

const PREFIX = 'ProviderPricing';

const classes = {
  container: `${PREFIX}-container`,
  header: `${PREFIX}-header`,
  title: `${PREFIX}-title`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  [`& .${classes.header}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(3),
  },
  [`& .${classes.title}`]: {
    fontFamily: 'Bitter',
    fontWeight: 700,
    fontSize: 30,
  },
}));

interface ProviderPricingProps {
  provider: Provider | BaseProvider;
}

export default function ProviderPricing({ provider }: ProviderPricingProps) {
  const t = useCommonTranslation();
  const dispatch = useAppDispatch();
  const isProvider = !!useLoggedInProviderId();
  const isPRUser = useIsPRUser();

  const providerServicePricings = useAppSelector(
    accountingSlice.selectAllProviderServicePricings,
  );
  const pricingModels = useAppSelector(
    accountingSlice.selectActivePricingModels,
  );

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = React.useState(false);
  const [editedPricing, setEditedPricing] =
    React.useState<ProviderServicePricing>();
  const [deletedPricing, setDeletedPricing] =
    React.useState<ProviderServicePricing>();
  const [isDeactivate, setIsDeactivate] = React.useState(false);
  const [activateDialogTitle, setActivateDialogTitle] = React.useState('');
  const [activateDialogMessage, setActivateDialogMessage] = React.useState('');
  const [activateDialogPostiveFn, setActivateDialogPostiveFn] = React.useState<
    () => void
  >(() => null);
  const [activateDialogNegativeFn, setActivateDialogNegativeFn] =
    React.useState<() => void>(() => null);
  const [activateDialogOpen, setActivateDialogOpen] = React.useState(false);
  const [activateDialogMinEndDateISO, setActivateDialogMinEndDateISO] =
    React.useState<string | undefined>(undefined);

  React.useEffect(() => {
    dispatch(accountingSlice.fetchTransportationServicesAsync());
    dispatch(accountingSlice.fetchPricingModelsAsync());
    dispatch(accountingSlice.fetchPricingFunctionsAsync());
  }, []);

  React.useEffect(() => {
    if (provider.transportationProviderID) {
      dispatch(
        accountingSlice.fetchProviderServicePricingsAsync(
          provider.transportationProviderID,
        ),
      );
    }
  }, [provider.transportationProviderID]);

  const close = () => {
    setDialogOpen(false);
  };

  const onCreate = () => {
    setEditedPricing(undefined);
    setDialogOpen(true);
  };

  const onEdit = (evt: any, providerServicePricing: ProviderServicePricing) => {
    evt.preventDefault();
    evt.stopPropagation();
    setEditedPricing(providerServicePricing);
    setDialogOpen(true);
  };

  const updatePricingEffectiveDates = (
    providerServicePricingUUID: string,
    start?: string,
    end?: string,
  ) => {
    dispatch(
      accountingSlice.updateProviderServicePricingEffectiveDatesAsync(
        providerServicePricingUUID,
        start,
        end,
      ),
    );
  };

  const activatePricing = (evt: any, pricing: ProviderServicePricing) => {
    evt.preventDefault();
    evt.stopPropagation();

    const confirm = (start?: string, end?: string) => {
      updatePricingEffectiveDates(
        pricing.providerServicePricingUUID!,
        start,
        end,
      );
      setActivateDialogOpen(false);
    };
    const cancel = () => {
      setActivateDialogOpen(false);
    };
    setIsDeactivate(false);
    setActivateDialogMinEndDateISO(undefined);
    setActivateDialogTitle(t('provider.confirmActivatePricingTitle'));
    setActivateDialogMessage(t('provider.confirmActivatePricingMessage'));
    setActivateDialogPostiveFn(() => confirm);
    setActivateDialogNegativeFn(() => cancel);
    setActivateDialogOpen(true);
  };

  const deactivatePricing = (evt: any, pricing: ProviderServicePricing) => {
    evt.preventDefault();
    evt.stopPropagation();

    const confirm = (start?: string, end?: string) => {
      updatePricingEffectiveDates(
        pricing.providerServicePricingUUID!,
        start,
        end,
      );
      setActivateDialogOpen(false);
    };
    const cancel = () => {
      setActivateDialogOpen(false);
    };
    setIsDeactivate(true);
    setActivateDialogMinEndDateISO(pricing.effectiveStartDate);
    setActivateDialogTitle(t('provider.confirmDeactivatePricingTitle'));
    setActivateDialogMessage(t('provider.confirmDeactivatePricingMessage'));
    setActivateDialogPostiveFn(() => confirm);
    setActivateDialogNegativeFn(() => cancel);
    setActivateDialogOpen(true);
  };

  const onDelete = (evt: any, psp: ProviderServicePricing) => {
    evt.preventDefault();
    evt.stopPropagation();
    setDeletedPricing(psp);
    setConfirmDialogOpen(true);
  };

  const confirmDelete = () => {
    if (deletedPricing) {
      dispatch(
        accountingSlice.deleteProviderServicePricingAsync(
          deletedPricing.providerServicePricingUUID,
        ),
      );
    }
    setConfirmDialogOpen(false);
  };

  const cancelDelete = () => {
    setConfirmDialogOpen(false);
  };

  return (
    <StyledBox className={classes.container}>
      <Box className={classes.header}>
        <Typography variant="h3" className={classes.title}>
          {t('provider.servicePricing')}
        </Typography>
        {!isProvider && isPRUser && (
          <Button variant="contained" color="primary" onClick={onCreate}>
            {t('provider.createServicePricing')}
          </Button>
        )}
      </Box>
      <ProviderPricingList
        providerServicePricings={providerServicePricings}
        pricingModels={pricingModels}
        readonly={isProvider}
        onEdit={onEdit}
        onActivate={activatePricing}
        onDeactivate={deactivatePricing}
        onDelete={onDelete}
      />
      <ProviderPricingDialog
        provider={provider}
        providerServicePricing={editedPricing}
        pricingModels={pricingModels}
        open={dialogOpen}
        close={close}
        title={
          editedPricing
            ? t('provider.updateServicePricing')
            : t('provider.createServicePricing')
        }
      />
      <ConfirmationDialog
        title={t('provider.confirmDeletePricingTitle')}
        message={t('provider.confirmDeletePricingMessage')}
        open={confirmDialogOpen}
        positiveAction={confirmDelete}
        negativeAction={cancelDelete}
      />
      <ActivateDialog
        title={activateDialogTitle}
        message={activateDialogMessage}
        open={activateDialogOpen}
        forActivate={!isDeactivate}
        minEndDateISO={activateDialogMinEndDateISO}
        handleOk={activateDialogPostiveFn}
        handleCancel={activateDialogNegativeFn}
      />
    </StyledBox>
  );
}
